import React, {useState, useEffect, useMemo, useCallback} from "react"
import {auth, database} from "../firebase"
import {ref, get, set, update} from "firebase/database"
import {useFinanceDataContext} from "../contexts/FinanceDataContext"
import TransactionDetails from "./TransactionDetails"
import FloatingActionButton from "./FloatingActionButton"
import ExpenseForm from "./ExpenseForm"
import IncomeForm from "./IncomeForm"
import BalancePopup from "./BalancePopup"
import WelcomeModal from "./WelcomeModal"
import "./ForecastBanner.css"
import CalendarControls from "./CalendarControls"
import CalendarGrid from "./CalendarGrid"
import {useNavigate} from "react-router-dom"
import moment from "moment"
import userJourneyTracker from "../hooks/user-journey-tracker";
import { useTheme } from '../contexts/ThemeContext';

const CalendarScreen = () => {
    const navigate = useNavigate()
    const [user, setUser] = useState(null)
    const [loading, setLoading] = useState(true)
    const [subscriptionStatus, setSubscriptionStatus] = useState(null)
    const [currentMonth, setCurrentMonth] = useState(new Date())
    const [calendarDays, setCalendarDays] = useState([])
    const [selectedTransaction, setSelectedTransaction] = useState(null)
    const [showTransactionForm, setShowTransactionForm] = useState(false)
    const [transactionType, setTransactionType] = useState(null)
    const [selectedDate, setSelectedDate] = useState(null)
    const [showBalancePopup, setShowBalancePopup] = useState(false)
    const [showWelcomeModal, setShowWelcomeModal] = useState(false)
    const [fabIsOpen, setFabIsOpen] = useState(false);

    // Only pass user to the hook when authenticated
    const {
        getItemsForDateRange,
        projectedBalances,
        formatAmount,
        getCategoryColor,
        getCategoryEmoji,
        loading: financeLoading,
        addItem,
        editItem, // Note the function name change to match the hook
        deleteItem,
        calculateProjectedBalances,
    } = useFinanceDataContext()
  const { theme } = useTheme();
    


    const [showAppModal, setShowAppModal] = useState(false)

    // Helper to check if a date is today
    const isToday = useCallback((date) => {
        const today = new Date()
        return (
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear()
        )
    }, [])
    const isMobileDevice = () => {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
        )
    }

    // Get all finance items for the current month view - only if user is authenticated
    const currentMonthItems = useMemo(() => {
        if (calendarDays.length === 0) return {}

        // Get the first and last day from the calendar days array
        const firstDay = calendarDays[0].date
        const lastDay = calendarDays[calendarDays.length - 1].date

        // Get all finance items for this date range
        const items = getItemsForDateRange(firstDay, lastDay)
        if (!items || items.length === 0) return {} // Add extra check for empty items

        // Filter out budget items - only include income, expense, and balance items
        const filteredItems = items.filter(
            (item) => item.type !== "budget" && item.type !== "balance"
        )
        // Group items by date
        const groupedItems = {}
        filteredItems.forEach((item) => {
            const dateStr = new Date(item.date).toISOString().split("T")[0]
            if (!groupedItems[dateStr]) {
                groupedItems[dateStr] = []
            }
            groupedItems[dateStr].push(item)
        })

        return groupedItems
    }, [calendarDays, getItemsForDateRange, ])

    // Define generateCalendarDays with useCallback to avoid circular dependencies
    const hasUnpaidTransactions = useCallback(
        (dateStr) => {
            if (!user || !currentMonthItems[dateStr]) return false
            return currentMonthItems[dateStr].some((item) => !item.isPaid)
        },
        [user, currentMonthItems]
    )

    // Define generateCalendarDays with useCallback to avoid circular dependencies
    const generateCalendarDays = useCallback(() => {
        const today = new Date()
        today.setHours(0, 0, 0, 0) // Reset time portion for accurate comparison

        const year = currentMonth.getFullYear()
        const month = currentMonth.getMonth()

        const currentDate = new Date(today)
        const dayOfWeek = currentDate.getDay() // 0 for Sunday, 1 for Monday, etc.
        // Adjust calculation to start week on Monday
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
        currentDate.setDate(currentDate.getDate() - daysFromMonday) // Go back to Monday
        // Check if we're viewing the current month
        const isCurrentMonthView =
            year === today.getFullYear() && month === today.getMonth()

        // For current month, start with current week
        // For other months, start with first day of month
        let startDate
        if (isCurrentMonthView) {
            startDate = new Date(currentDate)
        } else {
            // For past months, start with first day
            // For future months, also start with first day
            startDate = new Date(year, month, 1)

            // Go back to the nearest Sunday to include the first week fully
            const startDayOfWeek = startDate.getDay()
            if (startDayOfWeek > 0) {
                startDate.setDate(startDate.getDate() - startDayOfWeek)
            }
        }

        // Create array of calendar days
        const days = []

        // Fill in 6 rows of 7 days (42 days total) starting from our startDate
        for (let i = 0; i < 42; i++) {
            const date = new Date(startDate)
            date.setDate(startDate.getDate() + i)

            const dateString = date.toISOString().split("T")[0]
            const isPrevMonth =
                date.getMonth() < month ||
                (date.getMonth() === 11 &&
                    month === 0 &&
                    date.getFullYear() < year)
            const isCurrentMonth =
                date.getMonth() === month && date.getFullYear() === year
            const isNextMonth =
                date.getMonth() > month ||
                (date.getMonth() === 0 &&
                    month === 11 &&
                    date.getFullYear() > year)
            const isPastDate = date < today

            // Don't call hasUnpaidTransactions here - implement the check directly
            // to avoid circular dependencies
            const hasUnpaid =
            isPastDate &&
            dateString &&
            currentMonthItems[dateString]
                ? currentMonthItems[dateString].some((item) => !item.isPaid)
                : false;

            // When not authenticated, don't hide any days
            const shouldInclude = !isPastDate || (isPastDate && hasUnpaid) || !isCurrentMonth;


            if (shouldInclude) {
                days.push({
                    date: date,
                    dateString: dateString,
                    isPrevMonth: isPrevMonth,
                    isCurrentMonth: isCurrentMonth,
                    isNextMonth: isNextMonth,
                    isPastDate: isPastDate,
                    isToday: isToday(date),
                })
            } else {
                // For past dates with no unpaid items, push a placeholder
                days.push({
                    date: date,
                    dateString: dateString,
                    isPrevMonth: isPrevMonth,
                    isCurrentMonth: isCurrentMonth,
                    isNextMonth: isNextMonth,
                    isPastDate: true,
                    isHidden: true, // Mark as hidden
                    isToday: false,
                })
            }
        }

        setCalendarDays(days)
    }, [currentMonth, isToday])

    // In CalendarScreen.js, update the auth effect and welcome modal handling

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            console.log("CALENDAR AUTH STATE CHANGE:", {
                userAuthenticated: !!user,
                userId: user?.uid,
                calendarDaysExist: calendarDays.length > 0,
                timestamp: new Date().toISOString(),
            });
    
            if (user) {
                setUser(user);
                
                // Start tracking user journey
                await userJourneyTracker.initialize();
                userJourneyTracker.trackStep("CalendarScreen", "LoginSuccess");
    
                // Initialize user data if needed
                await initializeUserData(user.uid);
    
                // Fetch user data
                const userRef = ref(database, `users/${user.uid}`);
                const snapshot = await get(userRef);
                const userData = snapshot.val();
                
                if (userData) {
                    setSubscriptionStatus(userData.subscriptionStatus);
                    
                    // Check if this is a new session - for app modal
                    const hasSeenModal = localStorage.getItem("hasSeenAppModal");
                
                    // Only show app modal after successful login if it's a new session
                    if (!hasSeenModal) {
                        setTimeout(() => {
                            setShowAppModal(true);
                        }, 300);
                    } else {
                        setShowAppModal(false);
                    }
                }
    
                // Also make sure user is recorded in metrics for Today's users if they're new
                const initialClicksRef = ref(database, `metrics/initialClicks/${user.uid}`);
                const initialClicksSnapshot = await get(initialClicksRef);
                
                if (!initialClicksSnapshot.exists()) {
                    // New user - create the initial clicks entry with metadata
                    await set(initialClicksRef, {
                        email: user.email || null,
                        displayName: user.displayName || null,
                        creationTime: Date.now(), // Current timestamp for today's users filtering
                        completed: false,
                        clicks: {}
                    });
                }
    
                // Add timeout to ensure data is loaded completely before calculating
                setTimeout(() => {
                    // Force a recalculation of data when user signs in
                    if (calendarDays.length > 0) {
                        const firstDay = calendarDays[0].date;
                        const lastDay = calendarDays[calendarDays.length - 1].date;
                        calculateProjectedBalances(firstDay, lastDay);
                    }
                }, 1000); // 1 second delay
            } else {
                setUser(null);
            }
            setLoading(false);
        });
    
        // Clear the session flag when component unmounts
        return () => {
            unsubscribe();
            userJourneyTracker.endSession();
        };
    }, []);   // Generate calendar days for the current month view, starting with the current week
    useEffect(() => {
        generateCalendarDays()
    }, [generateCalendarDays])

    // Calculate projected balances for the current view - only if user is authenticated
    useEffect(() => {
        if (calendarDays.length > 0) {
            const firstDay = calendarDays[0].date
            const lastDay = calendarDays[calendarDays.length - 1].date
    
            // Make sure calculateProjectedBalances is available and call it
            if (typeof calculateProjectedBalances === "function") {
                calculateProjectedBalances(firstDay, lastDay)
            }
        }
    }, [calendarDays, calculateProjectedBalances,])

    // Add a function to handle navigation to mobile app
    // Update the handleContinueToWeb function in CalendarScreen.js
    const handleContinueToWeb = async () => {
        userJourneyTracker.trackStep("WelcomeModal", "ContinueToWeb");

        // Force refresh of data
        if (calendarDays.length > 0) {
            const firstDay = calendarDays[0].date;
            const lastDay = calendarDays[calendarDays.length - 1].date;
            calculateProjectedBalances(firstDay, lastDay);
        }
    
        // Mark app modal as seen for this session
        localStorage.setItem("hasSeenAppModal", "true");
        
        // Close the app modal first
        setShowAppModal(false);
    
        // ONLY then check if we should show the welcome modal
        // This ensures the welcome modal doesn't show before users make their app/web choice
        if (user) {
            try {
                const userRef = ref(database, `users/${user.uid}`);
                const snapshot = await get(userRef);
                const userData = snapshot.val();
    
                // Only show welcome modal if the flag is explicitly false
                // If it's true or undefined/null, don't show it
                if (userData && userData.hasSeenWelcomeModal === false) {
                    console.log("Showing welcome modal - flag is explicitly false");
                    // Add a small delay to ensure UI transition is smooth
                    setTimeout(() => {
                        setShowWelcomeModal(true);
                    }, 300);
                } else {
                    console.log("Not showing welcome modal - flag is:", userData?.hasSeenWelcomeModal);
                }
            } catch (error) {
                console.error("Error checking welcome modal status:", error);
            }
        }
    };
    // Add this function to CalendarScreen.js
    const initializeUserData = async (userId) => {
        try {
            // Check if user data is already initialized
            const profileSetupRef = ref(
                database,
                `users/${userId}/profileSetup`
            )
            const profileSetupSnapshot = await get(profileSetupRef)

            if (
                profileSetupSnapshot.exists() &&
                profileSetupSnapshot.val() === true
            ) {
                console.log("User data already initialized")
                return
            }

            // Set up initial user properties
            await set(
                ref(database, `users/${userId}/subscriptionStatus`),
                "inactive"
            )
            await set(ref(database, `users/${userId}/needsOnboarding`), true)
            // await set(ref(database, `users/${userId}/hasSeenWelcomeModal`), false);

            // Define the exact same initial budgets structure from CreateAccountScreen
            const initialBudgets = {
                "-OJACLkpA2FiJSyu0vjP": {
                    amount: 0,
                    color: "#FF7283",
                    createdAt: new Date().toISOString(),
                    date: moment().format("YYYY-MM-DD"),
                    emoji: "🏠",
                    isPaid: false,
                    title: "Housing",
                    type: "budget",
                },
                "-OJADQPMJvMQl5KBoaCc": {
                    amount: 0,
                    color: "#F5BF26",
                    createdAt: new Date().toISOString(),
                    date: moment().format("YYYY-MM-DD"),
                    emoji: "🍽️",
                    isPaid: false,
                    title: "Food",
                    type: "budget",
                },
                "-OJADR14tUaKW9n81Jnq": {
                    amount: 0,
                    color: "#039BE5",
                    createdAt: new Date().toISOString(),
                    date: moment().format("YYYY-MM-DD"),
                    emoji: "🚗",
                    isPaid: false,
                    title: "Transportation",
                    type: "budget",
                },
                "-OJADRmU8QSsNZoG9Cv5": {
                    amount: 0,
                    color: "#3F51B5",
                    createdAt: new Date().toISOString(),
                    date: moment().format("YYYY-MM-DD"),
                    emoji: "💡",
                    isPaid: false,
                    title: "Utilities",
                    type: "budget",
                },
                "-OJADWsblghS5A1r2_KM": {
                    amount: 0,
                    color: "#8E24AA",
                    createdAt: new Date().toISOString(),
                    date: moment().format("YYYY-MM-DD"),
                    emoji: "🎭",
                    isPaid: false,
                    title: "Entertainment",
                    type: "budget",
                },
                "-OJADftlmo7iSMzW6xiA": {
                    amount: 0,
                    color: "#5FD8D5",
                    createdAt: new Date().toISOString(),
                    date: moment().format("YYYY-MM-DD"),
                    emoji: "📚",
                    isPaid: false,
                    title: "Education",
                    type: "budget",
                },
                "-OJADhEHkUPyF8djIZqY": {
                    amount: 0,
                    color: "#795548",
                    createdAt: new Date().toISOString(),
                    date: moment().format("YYYY-MM-DD"),
                    emoji: "🏥",
                    isPaid: false,
                    title: "Healthcare",
                    type: "budget",
                },
                "-OJADiAfbiOaREufe-Ng": {
                    amount: 0,
                    color: "#E67C73",
                    createdAt: new Date().toISOString(),
                    date: moment().format("YYYY-MM-DD"),
                    emoji: "👤",
                    isPaid: false,
                    title: "Personal",
                    type: "budget",
                },
            }

            // Initialize the budgets under userId/flo/budgets
            await set(
                ref(database, `users/${userId}/flo/budgets`),
                initialBudgets
            )

            // Set profile setup flag
            await set(ref(database, `users/${userId}/profileSetup`), true)

            console.log("User data initialized successfully")
        } catch (error) {
            console.error("Error initializing user data:", error)
            throw error
        }
    }
    // Handler for "Get Mobile App" button
    const handleGetMobileApp = () => {
        userJourneyTracker.trackStep("WelcomeModal", "GetMobileApp");

        setShowAppModal(false)
        navigate("/dashboard")
    }

    // Get the projected balance for a specific date - safe when not authenticated
    const getProjectedBalance = (dateStr) => {
        if (Object.keys(projectedBalances).length === 0) {
            // Return exactly 0 if no projected balances
            return 0
        }

        // Original code for authenticated users with data
        const dates = Object.keys(projectedBalances).sort()
        const relevantDates = dates.filter((date) => date <= dateStr)

        if (relevantDates.length === 0) return 0 // Return 0 instead of null

        const closestDate = relevantDates[relevantDates.length - 1]
        return projectedBalances[closestDate] / 100
    }

    const handleTransactionClick = (transaction) => {
        if (!user) return // No actions for unauthenticated users
        setSelectedTransaction(transaction)
    }

    const handleCloseDetails = () => {
        setSelectedTransaction(null)
    }
    // In CalendarScreen.js, update the handleCloseWelcomeModal function

const handleCloseWelcomeModal = () => {
    userJourneyTracker.trackStep("WelcomeModal", "Close");

    setShowWelcomeModal(false);

    // Update the database to mark that the user has seen the welcome modal
    if (user) {
        const userRef = ref(database, `users/${user.uid}`);
        update(userRef, { hasSeenWelcomeModal: true })
            .then(() => {
                console.log("Welcome modal flag updated in database");
            })
            .catch((error) => {
                console.error("Error updating welcome modal flag:", error);
            });
    }
};
const handleFormSubmit = async (transaction) => {
    if (!user) return // No actions for unauthenticated users

    try {
        // If transaction has an id, it's an update
        if (transaction.id) {
            await editItem(transaction) // Use editItem instead of updateItem
        } else {
            // If a date was selected from the calendar, use that date
            if (selectedDate) {
                transaction.date = new Date(selectedDate).toISOString()
            }
            await addItem(transaction)
        }

        // Close the form and refresh
        setShowTransactionForm(false)
        setTransactionType(null)
        setSelectedDate(null)
    } catch (error) {
        console.error("Error saving transaction:", error)
        alert(
            "There was an error saving the transaction. Please try again."
        )
    }
}
const handleUpdateTransaction = (updatedTransaction) => {
    if (!user) return; // No actions for unauthenticated users
    
    // Close the details view
    setSelectedTransaction(null);
    
    // Force a refresh of the projected balances and data
    if (calendarDays.length > 0) {
        const firstDay = calendarDays[0].date;
        const lastDay = calendarDays[calendarDays.length - 1].date;
        
        // First ensure we have the most up-to-date data
        // This will trigger a re-fetch of items for the current date range
        getItemsForDateRange(firstDay, lastDay);
        
        // Then recalculate the projected balances
        calculateProjectedBalances(firstDay, lastDay);
        
        // Force a regeneration of calendar days to refresh UI
        generateCalendarDays();
    }
};

    const handleDeleteTransaction = (transaction, deleteType, deleteDate) => {
        if (!user) return // No actions for unauthenticated users

        console.log("CalendarScreen.handleDeleteTransaction called with:", {
            transactionId: transaction?.id,
            transactionType: transaction?.type,
            deleteType: deleteType,
            deleteDate: deleteDate,
            transactionKeys: transaction
                ? Object.keys(transaction)
                : "transaction is null",
        })

        // Check if we're trying to delete just the ID instead of the full object
        if (
            typeof transaction === "string" ||
            (transaction && !transaction.type && !transaction.id)
        ) {
            console.error(
                "ERROR: Attempting to delete with invalid transaction object",
                transaction
            )
            alert("Cannot delete: Invalid transaction data")
            return
        }

        try {
            // Ensure transaction object has required type property
            if (transaction && !transaction.type) {
                console.warn(
                    "Transaction missing type property - setting default type",
                    transaction
                )
                // Try to determine type from transaction properties
                if (transaction.budgetCategoryId) {
                    transaction.type = "expense"
                } else if (transaction.amount > 0) {
                    transaction.type = "income"
                } else {
                    console.error(
                        "Cannot determine transaction type from:",
                        transaction
                    )
                    alert("Error: Cannot determine transaction type")
                    return
                }
            }

            deleteItem(transaction, deleteType, deleteDate)
                .then(() => {
                    console.log("Transaction deleted successfully")
                    setSelectedTransaction(null)
                })
                .catch((error) => {
                    console.error("Error deleting transaction:", error)
                    alert(
                        "There was an error deleting the transaction. Please try again."
                    )
                })
        } catch (error) {
            console.error("Exception in handleDeleteTransaction:", error)
            alert(
                "An unexpected error occurred while deleting the transaction."
            )
        }
    }

    const nextMonth = () => {
        setCurrentMonth(
            new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
        )
    }

    const prevMonth = () => {
        setCurrentMonth(
            new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
        )
    }

    const formatDate = (date) => {
        const options = {month: "long", year: "numeric"}
        return date.toLocaleDateString("en-US", options)
    }

    // Functions for handling FAB actions
    const handleAddExpense = (date) => {
        if (!user) return // No actions for unauthenticated users

        setTransactionType("expense")
        setShowTransactionForm(true)
        if (date) {
            setSelectedDate(date)
        }
    }

    const handleAddIncome = (date) => {
        if (!user) return // No actions for unauthenticated users

        setTransactionType("income")
        setShowTransactionForm(true)
        if (date) {
            setSelectedDate(date)
        }
    }

    // Function to handle balance button press
    const handleBalancePress = () => {
        if (!user) return // No actions for unauthenticated users
        setShowBalancePopup(true)
    }

    // Function to close the balance popup
    const handleCloseBalancePopup = () => {
        setShowBalancePopup(false)
    }

    // Function to handle day click
    const handleDayClick = (day) => {
        if (!user) return; // No actions for unauthenticated users
    
        // Don't allow clicking on hidden days
        if (day.isHidden) return;
    
        // Set the selected date
        setSelectedDate(day.dateString);
        
        // Open the Floating Action Button menu
        setFabIsOpen(true);
        
        // No longer need the prompt dialog since we're using the FAB instead
        // We can focus on the area by scrolling to it if needed
        // Or add some visual indication that the day was selected
    };
    const findMinimumProjectedBalance = () => {
        if (Object.keys(projectedBalances).length === 0) return null

        // Look forward 30 days from today
        const today = new Date()
        const thirtyDaysLater = new Date()
        thirtyDaysLater.setDate(today.getDate() + 30)

        let minBalance = null
        let minDate = null

        // Sort the dates to ensure chronological order
        const sortedDates = Object.keys(projectedBalances).sort()

        for (const dateStr of sortedDates) {
            const date = new Date(dateStr)
            if (date >= today && date <= thirtyDaysLater) {
                const balance = projectedBalances[dateStr]

                if (minBalance === null || balance < minBalance) {
                    minBalance = balance
                    minDate = dateStr
                }
            }
        }

        return minBalance !== null ? {balance: minBalance, date: minDate} : null
    }

    // Function to handle forecast date press
    const handleForecastDatePress = (dateStr) => {
        if (!user) return // No actions for unauthenticated users

        // Find the day in the calendar that matches this date
        const targetDate = new Date(dateStr)

        // Ensure we're viewing the correct month
        const targetMonth = targetDate.getMonth()
        const targetYear = targetDate.getFullYear()

        // Set the current month to show the target date
        if (
            currentMonth.getMonth() !== targetMonth ||
            currentMonth.getFullYear() !== targetYear
        ) {
            setCurrentMonth(new Date(targetYear, targetMonth, 1))
        }

        // You could also add a scroll behavior here if needed
        // For example, highlight the day or scroll it into view
        console.log(`Navigating to date: ${dateStr}`)
    }

    if (loading || financeLoading) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                Loading...
            </div>
        )
    }
    return (
        <div style={{minHeight: "100vh", backgroundColor: theme.backgroundColor}}>
            {/* Main Content */}
            <div style={{}}>
                {/* Calendar Controls Component - Without navigation buttons */}
                <CalendarControls
                    currentMonth={currentMonth}
                    formatDate={formatDate}
                    handleBalancePress={handleBalancePress}
                    user={user}
                    subscriptionStatus={subscriptionStatus}
                    findMinimumProjectedBalance={findMinimumProjectedBalance}
                    handleForecastDatePress={handleForecastDatePress}
                />

                {/* Calendar Grid Component - With navigation buttons at the bottom */}
                <CalendarGrid
    calendarDays={calendarDays}
    currentMonthItems={currentMonthItems}
    formatAmount={formatAmount}
    getCategoryColor={getCategoryColor}
    getCategoryEmoji={getCategoryEmoji}
    getProjectedBalance={getProjectedBalance}
    handleDayClick={handleDayClick}
    handleTransactionClick={handleTransactionClick}
    user={user}
    currentMonth={currentMonth}
    prevMonth={prevMonth}
    nextMonth={nextMonth}
    formatDate={formatDate}
    findMinimumProjectedBalance={findMinimumProjectedBalance} // Add this prop
/>
            </div>

            {/* Transaction Details Modal - only if user is authenticated */}
            {user && selectedTransaction && (
                <TransactionDetails
                    item={selectedTransaction}
                    onClose={handleCloseDetails}
                    onUpdate={handleUpdateTransaction}
                    onDelete={handleDeleteTransaction}
                />
            )}

            {/* Transaction Form Modal - only if user is authenticated */}
            {user && showTransactionForm && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 2000,
                    }}
                >
                    {transactionType === "expense" ? (
                        <ExpenseForm
                            onSubmit={handleFormSubmit}
                            onCancel={() => {
                                setShowTransactionForm(false)
                                setTransactionType(null)
                                setSelectedDate(null)
                            }}
                            onDelete={handleDeleteTransaction}
                            initialValues={
                                selectedTransaction ||
                                (selectedDate
                                    ? {date: new Date(selectedDate)}
                                    : null)
                            }
                        />
                    ) : (
                        <IncomeForm
                            onSubmit={handleFormSubmit}
                            onCancel={() => {
                                setShowTransactionForm(false)
                                setTransactionType(null)
                                setSelectedDate(null)
                            }}
                            onDelete={handleDeleteTransaction}
                            initialValues={
                                selectedTransaction ||
                                (selectedDate
                                    ? {date: new Date(selectedDate)}
                                    : null)
                            }
                        />
                    )}
                </div>
            )}

            {/* Balance Popup Modal - only if user is authenticated */}
            {user && (
                <BalancePopup
                    visible={showBalancePopup}
                    onClose={handleCloseBalancePopup}
                    deleteItem={handleDeleteTransaction}
                />
            )}

            {/* Add the Floating Action Button - only if user is authenticated */}
    <FloatingActionButton
        onAddExpense={handleAddExpense}
        onAddIncome={handleAddIncome}
        isOpen={fabIsOpen}
        setIsOpen={setFabIsOpen}
        user={user}
    />
            {showAppModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.2)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            width: "90%",
                            maxWidth: "400px",
                            backgroundColor: theme.backgroundColor,
                            borderRadius: "20px",
                            padding: "24px",
                        }}
                    >
                        <h2
                            style={{
                                fontSize: "28px",
                                fontWeight: "700",
                                marginTop: 0,
                                marginBottom: "16px",
                                color: "#0F172A",
                            }}
                        >
    Welcome to Forecast 🚀
    </h2>

                        <p
                            style={{
                                fontSize: "16px",
                                color: "#64748B",
                                marginBottom: "24px",
                                lineHeight: "1.5",
                            }}
                        >
    Subscribe for $9.99/month or $79.99/year with a 14-day free trial!
    </p>

                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "16px",
                            }}
                        >
                            {isMobileDevice() ? (
                                // Mobile layout - prioritize mobile app
                                <>
                                    <button
                                        onClick={handleGetMobileApp}
                                        style={{
                                            width: "100%",
                                            height: "60px",
                                            backgroundColor: "#2563EB",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            borderRadius: "16px",
                                            border: "none",
                                            cursor: "pointer",
                                            boxShadow:
                                                "0 2px 4px rgba(0, 0, 0, 0.2)",
                                        }}
                                    >
                                        <span
                                            style={{
                                                color: "white",
                                                fontSize: "18px",
                                                fontWeight: "600",
                                            }}
                                        >
                                            Get Mobile App
                                        </span>
                                    </button>

                                    {/* <button
                                        onClick={handleContinueToWeb}
                                        style={{
                                            width: "100%",
                                            height: "50px",
                                            backgroundColor: "transparent",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            borderRadius: "16px",
                                            border: "1px solid #CBD5E1",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <span
                                            style={{
                                                color: "#64748B",
                                                fontSize: "16px",
                                                fontWeight: "500",
                                            }}
                                        >
                                            Continue to Web App
                                        </span>
                                    </button> */}
                                </>
                            ) : (
                                // Desktop layout - prioritize web app
                                <>
                                    <button
                                        onClick={handleContinueToWeb}
                                        style={{
                                            width: "100%",
                                            height: "60px",
                                            backgroundColor: "#2563EB",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            borderRadius: "16px",
                                            border: "none",
                                            cursor: "pointer",
                                            boxShadow:
                                                "0 2px 4px rgba(0, 0, 0, 0.2)",
                                        }}
                                    >
                                        <span
                                            style={{
                                                color: "white",
                                                fontSize: "18px",
                                                fontWeight: "600",
                                            }}
                                        >
                                            Continue to Web App
                                        </span>
                                    </button>

                                    <button
                                        onClick={handleGetMobileApp}
                                        style={{
                                            width: "100%",
                                            height: "50px",
                                            backgroundColor: "transparent",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            borderRadius: "16px",
                                            border: "1px solid #CBD5E1",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <span
                                            style={{
                                                color: "#64748B",
                                                fontSize: "16px",
                                                fontWeight: "500",
                                            }}
                                        >
                                            Get Mobile App
                                        </span>
                                    </button>
                                </>
                            )}

                            {/* Free Trial Button - Only show if user is not subscribed */}
                            {subscriptionStatus !== "active" && (
                                <button
                                    onClick={() => {
                                        setShowAppModal(false)
                                        navigate("/dashboard")
                                    }}
                                    style={{
                                        width: "100%",
                                        height: "50px",
                                        backgroundColor: "#10B981", // Green color for trial button
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        borderRadius: "16px",
                                        border: "none",
                                        cursor: "pointer",
                                        marginTop: "8px",
                                    }}
                                >
                                    <span
                                        style={{
                                            color: "white",
                                            fontSize: "16px",
                                            fontWeight: "600",
                                        }}
                                    >
    Start Free 14-Day Trial – $9.99/month or $79.99/year
    </span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <WelcomeModal
                visible={showWelcomeModal}
                onClose={handleCloseWelcomeModal}
            />
        </div>
    )
}

export default CalendarScreen